'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react'

import { LosiGamesOverlay } from '@/components/losi-games/losi-games-overlay'
import { LOSI_ARCADE_ENABLED } from '@/lib/losi-games/enabled'
import { LosiGamesTapRitual } from '@/lib/losi-games/ritual'
import type { LosiGameId, LosiGamesOpenOrigin, LosiGamesPhase } from '@/lib/losi-games/types'

interface LosiGamesContextValue {
  phase: LosiGamesPhase
  activeGame: LosiGameId | null
  tapProgress: number
  /** Call from any Losi avatar click. Counts toward Games; jiggle is handled by the mark. */
  registerLosiTap: (origin?: LosiGamesOpenOrigin) => void
  openGames: (origin?: LosiGamesOpenOrigin) => void
  closeGames: () => void
  playGame: (id: LosiGameId) => void
  backToHub: () => void
}

const LosiGamesContext = createContext<LosiGamesContextValue | null>(null)

export function useLosiGames(): LosiGamesContextValue {
  const ctx = useContext(LosiGamesContext)
  if (!ctx) {
    throw new Error('useLosiGames must be used within LosiGamesProvider')
  }
  return ctx
}

/** Safe for marks that may render outside the provider (tests / marketing). */
export function useLosiGamesOptional(): LosiGamesContextValue | null {
  return useContext(LosiGamesContext)
}

export function LosiGamesProvider({ children }: { children: ReactNode }) {
  const ritual = useRef(new LosiGamesTapRitual())
  const [phase, setPhase] = useState<LosiGamesPhase>('closed')
  const [activeGame, setActiveGame] = useState<LosiGameId | null>(null)
  const [origin, setOrigin] = useState<LosiGamesOpenOrigin>({ x: 0.5, y: 0.4 })
  const [tapProgress, setTapProgress] = useState(0)

  const openGames = useCallback((nextOrigin?: LosiGamesOpenOrigin) => {
    if (!LOSI_ARCADE_ENABLED) return
    if (nextOrigin) setOrigin(nextOrigin)
    setActiveGame(null)
    setPhase('expanding')
    ritual.current.reset()
    setTapProgress(0)
  }, [])

  const registerLosiTap = useCallback(
    (nextOrigin?: LosiGamesOpenOrigin) => {
      if (!LOSI_ARCADE_ENABLED) return
      if (phase !== 'closed') return
      const opened = ritual.current.tap()
      setTapProgress(ritual.current.count())
      if (opened) openGames(nextOrigin)
    },
    [openGames, phase]
  )

  const closeGames = useCallback(() => {
    setPhase('closed')
    setActiveGame(null)
    ritual.current.reset()
    setTapProgress(0)
  }, [])

  const playGame = useCallback((id: LosiGameId) => {
    setActiveGame(id)
    setPhase('playing')
  }, [])

  const backToHub = useCallback(() => {
    setActiveGame(null)
    setPhase('hub')
  }, [])

  const value = useMemo(
    () => ({
      phase,
      activeGame,
      tapProgress,
      registerLosiTap,
      openGames,
      closeGames,
      playGame,
      backToHub
    }),
    [phase, activeGame, tapProgress, registerLosiTap, openGames, closeGames, playGame, backToHub]
  )

  return (
    <LosiGamesContext.Provider value={value}>
      {children}
      {LOSI_ARCADE_ENABLED ? (
        <LosiGamesOverlay
          phase={phase}
          origin={origin}
          activeGame={activeGame}
          onExpanded={() => setPhase('hub')}
          onClose={closeGames}
          onPlay={playGame}
          onBack={backToHub}
        />
      ) : null}
    </LosiGamesContext.Provider>
  )
}
